import { Component, ComponentRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as Models from '../models/models-index';
import * as SharedServices from '../services/services-index';
import { ReportsService } from '../services/api/api-index';
import { PanelElementResolverService } from '../components/panels/panel-element-resolver.service';
import { DynamicComponentDirective } from '../directives/dynamic-component.directive';
import { RowPanelComponent } from '../components/panels/row-panel/row-panel.component';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, Subscription } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SharedHelpTextModalComponent } from '../modals/modals-index';
import { FilterActions } from '../filter/store/action-types';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../_store/app-state.model';
import { AppSelectors } from '../../_store/selector-types';
import { FilterSelectors } from '../filter/store';
import { ReportViewActions, ReportViewSelectors } from './store/';


@Component({
    selector: 'report-view',
    templateUrl: './report-view.component.html',
    styleUrls: ['./report-view.component.scss'],
    standalone: false
})
export class ReportViewComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  destroySubject = new Subject<void>();
  destroyed$ = this.destroySubject.asObservable();
  @ViewChild(DynamicComponentDirective, { static: true }) dynamicComponent!: DynamicComponentDirective;
  requestModel: Models.ReportRequestModel;
  viewContext: Models.ViewContext;
  componentRefs: ComponentRef<RowPanelComponent>[] = [];
  private reportViewConfig?: Models.ReportViewResponse;

  constructor(
    private resolverService: PanelElementResolverService,
    private reportService: ReportsService,
    private filterService: SharedServices.FilterService,
    private dialog: MatDialog,
    private translationService: SharedServices.TranslationService,
    private store$: Store,
  ) {
  }

  ngOnInit(): void {
    this.store$.select(AppSelectors.selectCurrentRouteData).pipe(
      tap(() => this.filterService.setActivePageTitle(' ')),
      switchMap(routeData => 
        // Get filters first
        this.getReportViewFilters(routeData.reportName).pipe(
          // Then get the filter request model
          switchMap(() => this.filterService.getReportViewFilterRequestModel(routeData.reportName)),
          // Use the filter request model to get the report view
          switchMap(requestModel => {
            this.requestModel = requestModel;
            return this.reportService.getReportView(requestModel).pipe(
              map(reportViewConfig => this.initializeReportView(reportViewConfig)),
              tap(() => this.loadDataSets(this.requestModel, this.componentRefs))
            );
          })
        )
      ),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  initializeReportView(config: Models.ReportViewResponse) {
    this.reportViewConfig = config;
    this.store$.dispatch(ReportViewActions.initializeReportView({ config }));

    this.viewContext = { reportName: config.reportName, selectedPerspective: config.perspectives[0], state: {} };
    this.filterService.setActivePageTitle(config.title);

    if (config.perspectives.length > 0) {
      // handle perspective change if there are any configured
      this.subscriptions.push(
        this.store$.select(ReportViewSelectors.selectCurrentPerspective({ reportName: config.reportName }))
          .pipe(
            tap(perspective => console.log('current perspective', perspective)),
            filter(perspective => perspective !== this.viewContext.selectedPerspective),
            tap(perspective => {
              this.viewContext.selectedPerspective = perspective;
              this.generatePanels(this.reportViewConfig!, this.viewContext);
              this.loadDataSets(this.requestModel, this.componentRefs);
            })
          ).subscribe()
      );
    }

    return this.generatePanels(config, this.viewContext);
  }

  getReportViewFilters(reportName: string): Observable<Models.ReportViewFilter[]> {
    return this.reportService.getReportFilterNames(reportName).pipe(
      tap(config => this.store$.dispatch(FilterActions.updateReportViewFilterConfiguration({ config }))),
      concatMap(() =>
        this.store$.select(FilterSelectors.selectReportViewFiltersForReport({ reportName })).pipe(
          filter(filters => filters.length > 0),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        )
      )
    );
  }

  resolvePanels(reportViewConfiguration: Models.ReportViewResponse, viewContext: Models.ViewContext): Models.Panel[] {
    if (!viewContext.selectedPerspective)
      return reportViewConfiguration.panels;

    const panels: Models.Panel[] = [];
    //evaluate each panel to see if it is supported for the current perspective, we need to not only see if it's a path match but then look at the panels configurations to see if they're in the 'items' array on the perspective selector
    reportViewConfiguration.panels.forEach(panel => {
      const selector = viewContext.selectedPerspective.selectors?.find((el: Models.PerspectiveSelector) => el.type === 'Panel' && el.path === panel.name);

      if (!selector)
        return;

      // filter out configurations that are not supported by the perspective and then order them based on the order in the perspective selector
      const supportedConfigurations = selector.items?.length === 0
        ? panel.configurations
        : panel.configurations
          .filter(config => selector.items.includes(config.name))
          .sort((a, b) => selector.items.indexOf(a.name) - selector.items.indexOf(b.name));

      // check the panel state in view context and if there isn't an entry for it, if not add it
      const selectionKey = `panel-${panel.name}-index`;
      if (!viewContext.state[selectionKey]) {
        viewContext.state[selectionKey] = supportedConfigurations[0].name;
      }

      if (supportedConfigurations.length > 0) {
        panels.push({
          ...panel,
          configurations: supportedConfigurations
        });
      }

    });

    return panels;
  }

  generatePanels(reportViewConfiguration: Models.ReportViewResponse, viewContext: Models.ViewContext): ComponentRef<RowPanelComponent>[] {
    const viewContainerRef = this.dynamicComponent.viewContainerRef;
    viewContainerRef.clear();

    const panels = this.resolvePanels(reportViewConfiguration, viewContext);

    const maxRow = Math.max(...panels.map(panel => panel.row));

    if (isNaN(maxRow)) {
      console.error("Max row count could not be determined.  Make sure your panel elements have a row value");
      return null;
    }

    let refs = [];
    refs = Array.from({ length: maxRow }, (_, i) => {
      const componentRef = viewContainerRef.createComponent(RowPanelComponent);
      componentRef.instance.rowIndex = i + 1;
      componentRef.instance.panels = panels.filter(panel => panel.row === i + 1);
      componentRef.instance.translationService = this.translationService;
      componentRef.instance.viewContext = viewContext;

      return componentRef;
    });

    this.componentRefs = refs;

    return refs;
  }

  private loadDataSets(requestModel: Models.ReportRequestModel, componentRefs: ComponentRef<RowPanelComponent>[]) {
    componentRefs.forEach(componentRef => {
      // report data changed so let's inform panels to start pulse animation
      componentRef.instance.dataSets = [];

      const rowPanels = componentRef.instance.panels;

      const updatedRequestModel: Models.ReportRequestModel = {
        ...requestModel,
        dataSets: this.getDataSetsToLoad(rowPanels)
      };

      this.reportService.getDataSets(updatedRequestModel)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(datasets => componentRef.instance.dataSets = datasets);
    });
  }

  // help
  openHelp(panelConfig: Models.Panel) {
    this.dialog.open(SharedHelpTextModalComponent, {
      width: '50vw',
      data: {
        title: panelConfig.configurations[0].helpTextTitle,
        name: panelConfig.configurations[0].helpTextKey
      }
    });
  }

  getInitialRequestModel(routeData): Models.ReportRequestModel {
    return {
      orderBy: '',
      orderAscending: false,
      reportType: routeData.reportName,
      filters: {
        startDate: new Date(),
        endDate: new Date(),
        culture: this.filterService.locale,
      },
    };
  }

  private getDataSetsToLoad(rowPanels: Models.Panel[]): string[] {
    const dataSetsToLoad = Array.from(
      new Set(
        rowPanels
          .flatMap(panel => panel.configurations[0]) // Get default configuration from all panels
          .flatMap(config => config.elements)        // Get all elements from default configurations
          .flatMap(element => element.settings.dataSets ?? [element.settings.dataSet]) // Get dataSets from each element
          .filter(dataSet => dataSet !== undefined)  // Filter out undefined datasets
      )
    );

    return dataSetsToLoad;
  }
}
